import { useCallback, useEffect, useState } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, List, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { ProfileDrawer } from "@/components/ProfileDrawer";
import { api, errMsg } from "@/lib/api";

const money = (v) => `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(2)}`;
/* Mobile calendar cells are ~45px wide — cents do not fit, so round the day's figure. */
const shortMoney = (v) => {
  const a = Math.abs(v);
  const s = a >= 1000 ? `${(a / 1000).toFixed(1)}k` : Math.round(a);
  return `${v >= 0 ? "+" : "-"}$${s}`;
};
const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CalendarGrid = ({ days, period, base }) => {
  const map = Object.fromEntries(days.map((d) => [d.day, d]));
  const now = base || new Date();
  let cells = [];
  if (period === "week") {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    cells = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  } else {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const total = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7;
    cells = [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: total }, (_, i) => new Date(now.getFullYear(), now.getMonth(), i + 1)),
    ];
  }

  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  return (
    <div data-testid="calendar-grid">
      <div className="grid grid-cols-7 gap-px mb-px">
        {WD.map((w) => (
          <div key={w} className="px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-400 text-center">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="border border-zinc-900 min-h-[64px]" />;
          const cell = map[iso(d)];
          const p = cell?.profit ?? null;
          const tone =
            p === null || p === 0
              ? "border-zinc-800 bg-zinc-900/30 text-zinc-400"
              : p > 0
              ? "border-emerald-800 bg-emerald-500/10 text-emerald-400"
              : "border-red-900 bg-red-500/10 text-red-400";
          return (
            <div
              key={iso(d)}
              data-testid={`calendar-day-${iso(d)}`}
              className={`border p-1.5 sm:p-2 min-h-[64px] min-w-0 overflow-hidden flex flex-col justify-between gap-0.5 transition-colors duration-200 ${tone}`}
            >
              <span className="font-mono text-[10px] sm:text-[11px] text-zinc-400">
                {d.getDate()}
              </span>
              {p !== null && (
                <>
                  <span className="sm:hidden font-mono text-[10px] leading-tight truncate">
                    {shortMoney(p)}
                  </span>
                  <span className="hidden sm:block font-mono text-sm leading-tight truncate">
                    {money(p)}
                  </span>
                </>
              )}
              {cell && (
                <span className="font-mono text-[9px] sm:text-[10px] text-zinc-400 truncate">
                  {cell.trades}
                  <span className="hidden sm:inline"> fills</span>
                  <span className="sm:hidden">f</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* Re-read one close from MT5: repairs a row booked without the broker's numbers. */
const RecheckButton = ({ dealId, onChanged }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      data-testid={`history-recheck-${dealId}`}
      title="Ask MT5 for this close again and rewrite the P&L (needs EA v2.15+)"
      onClick={async () => {
        setBusy(true);
        try {
          const { data } = await api.post(`/deals/${dealId}/recheck`);
          toast.success(
            data.profit === data.was
              ? `MT5 confirms ${money(data.profit)}`
              : `MT5 says ${money(data.profit)} (was ${money(data.was)})`);
          onChanged?.();
        } catch (e) {
          toast.error(errMsg(e));
        } finally {
          setBusy(false);
        }
      }}
      className="flex items-center gap-1.5 rounded-sm border border-zinc-700 px-2 py-1.5 font-mono text-[10px] uppercase tracking-wide text-zinc-300 hover:bg-zinc-800 hover:text-white disabled:opacity-40 transition-colors duration-200"
    >
      <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
      {busy ? "Asking…" : "Recheck"}
    </button>
  );
};

const DealRow = ({ d, onChanged }) => {
  const [open, setOpen] = useState(false);
  const pnl = d.profit > 0 ? "text-emerald-400" : d.profit < 0 ? "text-red-400" : "text-zinc-200";

  return (
    <div data-testid={`history-row-${d.deal_id}`}>
      <button
        type="button"
        data-testid={`history-row-toggle-${d.deal_id}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-3 text-left transition-colors duration-200 active:bg-zinc-800/40"
      >
        <span className="font-mono text-xs font-semibold text-zinc-100 shrink-0">{d.symbol}</span>
        <span
          className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold shrink-0 ${
            d.side === "buy"
              ? "bg-emerald-500/10 text-emerald-400"
              : d.side === "sell"
              ? "bg-red-500/10 text-red-400"
              : "bg-zinc-800 text-zinc-400"
          }`}
        >
          {d.side || "—"}
        </span>
        <span
          className={`font-mono text-xs font-bold ml-auto shrink-0 ${pnl}`}
          data-testid={`history-pnl-${d.ticket}`}
        >
          {d.estimated && (
            <span
              className="text-zinc-500 mr-0.5"
              title="Waiting on MT5 to confirm the fill"
              data-testid={`history-pnl-estimated-${d.ticket}`}
            >
              ≈
            </span>
          )}
          {money(d.profit)}
        </span>
        <span className="font-mono text-[10px] text-zinc-500 shrink-0">
          {(d.closed_at_ist || d.day || "").slice(5, 16)}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-zinc-500 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-zinc-800 bg-black/40 px-3 py-3"
          data-testid={`history-detail-${d.deal_id}`}
        >
          <Detail label="Closed (IST)" value={d.closed_at_ist || d.day} />
          <Detail label="Reason" value={(d.kind || "—").toUpperCase()} />
          <Detail label="Lot size" value={d.volume} />
          <Detail label="Source" value={d.source || "Strict RR"} />
          <Detail label="Entry" value={d.entry} />
          <Detail label="Exit" value={d.exit_price} />
          <div className="col-span-2 flex">
            <RecheckButton dealId={d.deal_id} onChanged={onChanged} />
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{label}</p>
    <p className="font-mono text-[11px] text-zinc-200 truncate">{value || "—"}</p>
  </div>
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/* Any month, any year — traders review old months, not just the running one. */
const MonthPicker = ({ value, onChange, onClear }) => {
  const today = new Date();
  const [y, m] = (value || ymOf(today)).split("-").map(Number);
  const shift = (delta) => onChange(ymOf(new Date(y, m - 1 + delta, 1)));
  const years = Array.from({ length: 7 }, (_, i) => today.getFullYear() - 5 + i);
  const selCls =
    "rounded-sm bg-zinc-900/60 border border-zinc-700 px-2 py-2 font-mono text-xs text-zinc-100 focus:border-emerald-500 focus:outline-none";
  const navCls =
    "p-2 border border-zinc-700 rounded-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors duration-200";

  return (
    <div className="flex items-center gap-1.5" data-testid="month-picker">
      <button onClick={() => shift(-1)} className={navCls} data-testid="month-prev-btn"
              aria-label="Previous month">
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <select
        className={selCls}
        data-testid="month-select"
        value={m}
        onChange={(e) => onChange(ymOf(new Date(y, Number(e.target.value) - 1, 1)))}
      >
        {MONTHS.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        className={selCls}
        data-testid="year-select"
        value={y}
        onChange={(e) => onChange(ymOf(new Date(Number(e.target.value), m - 1, 1)))}
      >
        {years.map((yr) => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </select>
      <button onClick={() => shift(1)} className={navCls} data-testid="month-next-btn"
              aria-label="Next month">
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
      {value && (
        <button onClick={onClear} data-testid="month-clear-btn"
                className="px-2 py-2 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold tracking-wide text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors duration-200">
          Clear
        </button>
      )}
    </div>
  );
};

export default function History() {
  const [tab, setTab] = useState("log");
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [cal, setCal] = useState(null);
  const [status, setStatus] = useState(null);
  const [profile, setProfile] = useState(false);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    const span = month ? `month=${month}` : `period=${period}`;
    const [h, c, st, s, sum] = await Promise.all([
      api.get(`/history?${span}&page=${page}&per_page=10`),
      api.get(`/calendar?${span}`),
      api.get("/mt5/status"),
      api.get("/settings/effective"),
      api.get("/summary"),
    ]);
    setData(h.data);
    setCal(c.data);
    setStatus(st.data);
    setSettings(s.data);
    setSummary(sum.data);
  }, [period, page, month]);

  useEffect(() => {
    load();
  }, [load]);

  const tabCls = (active) =>
    `flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] font-semibold rounded-sm border transition-colors duration-200 ${
      active
        ? "border-emerald-600 bg-emerald-600/15 text-emerald-300"
        : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-200"
    }`;

  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-400 whitespace-nowrap";
  const td = "px-3 py-2.5 font-mono text-xs text-zinc-200 whitespace-nowrap";

  return (
    <Shell status={status} onProfile={() => setProfile(true)}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              Trade History
            </h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              {data ? `${data.total} closed fills · realised ${money(data.realised)}` : "—"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className={tabCls(tab === "log")} onClick={() => setTab("log")} data-testid="tab-log">
              <List className="h-3.5 w-3.5" /> Log
            </button>
            <button className={tabCls(tab === "calendar")} onClick={() => setTab("calendar")} data-testid="tab-calendar">
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </button>
            <span className="hidden sm:block w-px h-6 bg-zinc-800 mx-1" />
            {["week", "month", "all"].map((p) => (
              <button
                key={p}
                data-testid={`period-${p}-btn`}
                onClick={() => {
                  setPeriod(p);
                  setMonth(null);
                  setPage(1);
                }}
                className={tabCls(!month && period === p)}
              >
                {p}
              </button>
            ))}
            <span className="hidden sm:block w-px h-6 bg-zinc-800 mx-1" />
            <MonthPicker
              value={month}
              onChange={(ym) => {
                setMonth(ym);
                setPage(1);
              }}
              onClear={() => {
                setMonth(null);
                setPage(1);
              }}
            />
          </div>
        </div>

        {tab === "log" ? (
          <div className="border border-zinc-800 bg-zinc-900/30" data-testid="history-panel">
            {/* mobile: one line per fill, tap to reveal the rest */}
            <div className="md:hidden divide-y divide-zinc-800">
              {data?.items?.length === 0 && (
                <p className="px-4 py-10 text-center text-xs text-zinc-400" data-testid="no-history">
                  No closed trades in this period.
                </p>
              )}
              {data?.items?.map((d) => (
                <DealRow key={d.deal_id} d={d} onChanged={load} />
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse min-w-[860px]">
                <thead className="bg-black/40">
                  <tr>
                    {["Closed (IST)", "Symbol", "Side", "Size", "Entry", "Exit", "Reason", "Source", "P&L", ""].map(
                      (h) => (
                        <th key={h} className={th}>
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-xs text-zinc-400" data-testid="no-history">
                        No closed trades in this period.
                      </td>
                    </tr>
                  )}
                  {data?.items?.map((d) => (
                    <tr
                      key={d.deal_id}
                      data-testid={`history-row-${d.deal_id}`}
                      className="border-b border-zinc-800 hover:bg-zinc-800/40 transition-colors duration-200"
                    >
                      <td className={`${td} text-zinc-400`}>{d.closed_at_ist || d.day}</td>
                      <td className={`${td} text-zinc-100 font-semibold`}>{d.symbol}</td>
                      <td className={td}>
                        <span
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${
                            d.side === "buy"
                              ? "bg-emerald-500/10 text-emerald-400"
                              : d.side === "sell"
                              ? "bg-red-500/10 text-red-400"
                              : "bg-zinc-800 text-zinc-400"
                          }`}
                        >
                          {d.side || "—"}
                        </span>
                      </td>
                      <td className={td}>{d.volume}</td>
                      <td className={td}>{d.entry || "—"}</td>
                      <td className={td}>{d.exit_price || "—"}</td>
                      <td className={`${td} text-zinc-400 uppercase text-[10px] tracking-wide`}>
                        {d.kind}
                      </td>
                      <td className={td}>
                        <span
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${
                            d.source === "External"
                              ? "bg-amber-500/10 text-amber-400"
                              : "bg-emerald-500/10 text-emerald-300"
                          }`}
                        >
                          {d.source || "Strict RR"}
                        </span>
                      </td>
                      <td
                        className={`${td} ${
                          d.profit > 0 ? "text-emerald-400" : d.profit < 0 ? "text-red-400" : ""
                        }`}
                        data-testid={`history-pnl-${d.ticket}`}
                      >
                        {d.estimated && (
                          <span
                            className="text-zinc-500 mr-0.5"
                            title="Waiting on MT5 to confirm the fill — this figure may shift by a few cents"
                            data-testid={`history-pnl-estimated-${d.ticket}`}
                          >
                            ≈
                          </span>
                        )}
                        {money(d.profit)}
                      </td>
                      <td className={td}>
                        <RecheckButton dealId={d.deal_id} onChanged={load} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-400 font-mono" data-testid="pagination-info">
                Page {data?.page ?? 1} of {data?.pages ?? 1}
              </span>
              <div className="flex gap-2">
                <button
                  data-testid="prev-page-btn"
                  disabled={(data?.page ?? 1) <= 1}
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-3 py-1.5 rounded-sm text-xs uppercase font-semibold tracking-wide transition-colors duration-200 disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  data-testid="next-page-btn"
                  disabled={(data?.page ?? 1) >= (data?.pages ?? 1)}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-3 py-1.5 rounded-sm text-xs uppercase font-semibold tracking-wide transition-colors duration-200 disabled:opacity-40"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-zinc-800 bg-zinc-900/30 p-3 sm:p-4" data-testid="calendar-panel">
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4 font-mono text-xs">
              <span className="text-zinc-400" data-testid="calendar-heading">
                {month
                  ? `${MONTHS[Number(month.split("-")[1]) - 1]} ${month.split("-")[0]}`
                  : period === "all"
                  ? "All time"
                  : `This ${period}`}
              </span>
              <span className="text-zinc-400">
                Net{" "}
                <span className={cal?.total >= 0 ? "text-emerald-400" : "text-red-400"}>
                  {cal ? money(cal.total) : "—"}
                </span>
              </span>
              <span className="text-zinc-400">
                Green days <span className="text-emerald-400">{cal?.green_days ?? 0}</span>
              </span>
              <span className="text-zinc-400">
                Red days <span className="text-red-400">{cal?.red_days ?? 0}</span>
              </span>
            </div>
            {cal && (
              <CalendarGrid
                days={cal.days}
                period={!month && period === "week" ? "week" : "month"}
                base={month ? new Date(Number(month.split("-")[0]),
                                       Number(month.split("-")[1]) - 1, 1) : null}
              />
            )}
          </div>
        )}
      </div>

      <ProfileDrawer
        open={profile}
        onClose={() => setProfile(false)}
        status={status}
        settings={settings}
        summary={summary}
        onChange={load}
      />
    </Shell>
  );
}
